import {
    Component,
    OnDestroy,
    ChangeDetectionStrategy,
    inject,
} from '@angular/core';
import { filter, forkJoin, Observable, Subject, takeUntil, tap } from 'rxjs';
import { ServiceFulfillmentService } from '../services/service-fulfillment.service';
import { GridFilterDataService } from '../../../core/services/grid-filter-data/grid-filter-data.service';
import { ApplicationGridService } from '../../shared/services/application-grid.service';
import { ServiceFulfillmentGridRow } from '../models/service-fulfillment-grid-row';
import {
    ColDef,
    GridApi,
    GridOptions,
    GridReadyEvent,
    IRowNode,
} from 'ag-grid-community';
import { FilterOption } from '../../../core/models/grid/filter-option';
import { GridToolbarOptions } from '../../../shared/components/grid/grid-toolbar/models/grid-toolbar-options';
import { GridHelper } from '../../../core/models/grid/grid-helper';
import { nameOf } from '../../../core/functions/string-helpers';
import { ServiceOrderCellRendererComponent } from '../../../shared/components/grid/cell-renderers/service-order-cell-renderer/service-order-cell-renderer.component';
import { ServiceOrderCellRendererParams } from '../../../shared/components/grid/cell-renderers/service-order-cell-renderer/models/service-order-cell-renderer-params';
import { MultiSelectFilterComponent } from '../../../shared/components/grid/multi-select-filter/multi-select-filter.component';
import { ApplicationIdCellRendererComponent } from '../../../shared/components/grid/cell-renderers/dashboard/application-id-cell-renderer/application-id-cell-renderer.component';
import { formatToDate } from '../../../core/functions/date-helpers';
import { ApplicationActionCellRendererComponent } from '../../../shared/components/grid/cell-renderers/dashboard/application-action-cell-renderer/application-action-cell-renderer.component';
import { WorkflowStateLabels } from '../constants/workflow-state';
import { GridToolbarComponent } from '../../../shared/components/grid/grid-toolbar/grid-toolbar.component';
import { AgGridAngular } from 'ag-grid-angular';
import { MatSelectModule } from '@angular/material/select';
import { UserStateService } from '../../user/shared/services/user-state/user-state.service';
import { MatFormFieldModule } from '@angular/material/form-field';
import { DashboardConstants } from '../../dashboard/constants/dashboard-constants';
import { RouteConstants } from '../../../core/constants/routes';
import { ApplicationStatusCellRendererComponent } from '../../../shared/components/grid/cell-renderers/application-status-cell-renderer/application-status-cell-renderer.component';
import { ApplicationStatusCellRendererParams } from '../../../shared/components/grid/cell-renderers/application-status-cell-renderer/models/application-status-cell-renderer-params';

export enum ServiceFilter {
    Mine = 'mine',
    Company = 'company',
}

@Component({
    selector: 'app-services-and-filings',
    imports: [
        AgGridAngular,
        GridToolbarComponent,
        MatSelectModule,
        MatFormFieldModule,
    ],
    templateUrl: './services-and-filings.component.html',
    changeDetection: ChangeDetectionStrategy.Eager,
    styleUrl: './services-and-filings.component.scss',
})
export class ServicesAndFilingsComponent implements OnDestroy {
    private destroy$ = new Subject<void>();

    private readonly serviceFulfillmentService = inject(
        ServiceFulfillmentService,
    );
    private readonly filterDataService = inject(GridFilterDataService);
    private readonly userStateService = inject(UserStateService);

    context: unknown;
    rowData: ServiceFulfillmentGridRow[] = [];
    gridApi: GridApi | undefined;

    servicesFilterOptions: FilterOption[] = [];
    advisorFilterOptions: FilterOption[] = [];
    statusFilterOptions: FilterOption[] =
        this.filterDataService.getServiceFulfillmentApplicationStatusFilterOptions();
    workflowStateFilterOptions: FilterOption[] =
        this.filterDataService.getWorkflowStateFilterOptions();
    handedOverFilterOptions: FilterOption[] =
        this.filterDataService.getHandedOverFilterOptions();
    externalFilterOptions: FilterOption[] = [
        {
            label: 'My Services',
            value: ServiceFilter.Mine,
            selected: true,
        },
        {
            label: 'Company Services',
            value: ServiceFilter.Company,
            selected: false,
        },
    ];

    columnDefs: ColDef[] = [];

    gridToolbarOptions: GridToolbarOptions = {
        ...GridHelper.defaultToolbarOptions,
        exportCsv: true,
        exportCsvOptions: {
            fileName: () =>
                GridHelper.getExportFileName('Services_and_Filings'),
            columnKeys: this.getColumnDefs()
                .filter(
                    (
                        col, // Export all columns except for Action column
                    ) =>
                        col.field !==
                        nameOf<ServiceFulfillmentGridRow>(
                            'isCoordinationAllowed',
                        ),
                )
                .map((col) => col.field as string),
        },
    };

    selectedService: ServiceFilter = ServiceFilter.Mine;

    private isExternalFilterPresent = (): boolean => {
        return this.selectedService === ServiceFilter.Mine;
    };

    private doesExternalFilterPass = (
        node: IRowNode<ServiceFulfillmentGridRow>,
    ): boolean => {
        const currentUsername = this.userStateService.getUser()?.username;

        // If the current user has not been loaded yet, do not apply the external filter.
        if (!currentUsername) {
            return true;
        }

        if (!node.data) {
            return true;
        }

        return node.data.customerUsername === currentUsername;
    };

    gridOptions: GridOptions = {
        ...GridHelper.defaultGridOptions,
        isExternalFilterPresent: this.isExternalFilterPresent,
        doesExternalFilterPass: this.doesExternalFilterPass,
    };

    onGridReady(params: GridReadyEvent<unknown>): void {
        this.gridApi = params.api;

        this.loadFilterData()
            .pipe(takeUntil(this.destroy$))
            .subscribe(() => {
                this.loadGridData();
            });
    }

    ngOnDestroy() {
        this.destroy$.next();
        this.destroy$.complete();
    }

    private getColumnDefs(): ColDef[] {
        return [
            {
                headerName: 'Service',
                field: nameOf<ServiceFulfillmentGridRow>('applicationPurpose'),
                cellRenderer: ServiceOrderCellRendererComponent,
                cellRendererParams: (params: ServiceOrderCellRendererParams) =>
                    ({
                        service: ApplicationGridService.formatServicesColumn(
                            params.data,
                        ),
                        orderId: params.data.orderId,
                        returnRoute:
                            RouteConstants.ServiceFulfillment.getFullRoute(
                                'INDEX',
                            ),
                    }) as ServiceOrderCellRendererParams,
                valueFormatter: (params) => {
                    return ApplicationGridService.formatServicesColumnWithOrder(
                        params.data,
                    );
                },
                valueGetter: (params) => {
                    return ApplicationGridService.getServiceValue(params.data);
                },
                maxWidth: 150,
                filter: MultiSelectFilterComponent,
                filterParams: {
                    filterOptions: this.servicesFilterOptions.map((option) => ({
                        label: option.label,
                        value: option.label,
                        selected: option.selected,
                    })),
                },
                getQuickFilterText: (params) => {
                    return ApplicationGridService.formatServicesColumnWithOrder(
                        params.data,
                    );
                },
            },
            {
                headerName: 'App ID /\nPSCA ID',
                field: nameOf<ServiceFulfillmentGridRow>('salmonLogId'),
                minWidth: 160,
                filter: true,
                cellRenderer: ApplicationIdCellRendererComponent,
                cellRendererParams: {
                    returnRoute:
                        RouteConstants.ServiceFulfillment.getFullRoute('INDEX'),
                },
                valueGetter: (params) => {
                    return ApplicationGridService.formatApplicationIdColumn(
                        params.data,
                    );
                },
            },
            {
                headerName: 'Call Sign /\nFile #',
                field: nameOf<ServiceFulfillmentGridRow>('callSign'),
                filter: true,
                cellStyle: {
                    whiteSpace: 'pre-line', // preserves \n characters
                },
                valueGetter: (params) => {
                    return ApplicationGridService.formatCallSignColumn(
                        params.data,
                    );
                },
                minWidth: 150,
            },
            {
                headerName: 'Applicant /\nTx Location',
                field: nameOf<ServiceFulfillmentGridRow>('licenseeEntityName'),
                filter: true,
                minWidth: 180,
                valueGetter: (params) => {
                    return ApplicationGridService.formatApplicantTransmissionLocationColumn(
                        params.data,
                    );
                },
            },
            {
                headerName: 'Customer',
                field: nameOf<ServiceFulfillmentGridRow>('organizationName'),
                filter: true,
                valueGetter: (params) => {
                    return ApplicationGridService.formatCustomerToUserThenCompany(
                        params.data,
                    );
                },
                minWidth: 160,
            },
            {
                headerName: 'Created',
                field: nameOf<ServiceFulfillmentGridRow>('createdAt'),
                filter: true,
                minWidth: 160,
                ...GridHelper.defaultDateColDef,
                valueGetter: (params) => {
                    const date =
                        params.data.submittedAt ?? params.data.createdAt;
                    return formatToDate(date);
                },
                filterParams: {
                    ...GridHelper.defaultDateFilterParams,
                    filterOptions: [
                        // Removed 'equals' and 'notEqual' since the date picker doesn't have time component
                        'lessThan',
                        'greaterThan',
                        'inRange',
                        'blank',
                        'notBlank',
                    ],
                },
            },
            {
                headerName: 'Advisor',
                field: nameOf<ServiceFulfillmentGridRow>('advisorId'),
                valueGetter: (params) => {
                    const rowData = params.data as ServiceFulfillmentGridRow;
                    const advisorLabel = this.advisorFilterOptions?.find(
                        (option) =>
                            option.value === rowData.advisorId?.toString(),
                    )?.label;
                    return advisorLabel ?? '';
                },
                filter: MultiSelectFilterComponent,
                filterParams: {
                    filterOptions: (this.advisorFilterOptions ?? []).map(
                        (option) => ({
                            label: option.label,
                            value: option.value === '' ? '' : option.label, // Ensure empty string value for 'None Assigned' option
                            selected: option.selected,
                        }),
                    ),
                },
            },
            {
                headerName: 'Status',
                field: nameOf<ServiceFulfillmentGridRow>('filingStatus'),
                cellRenderer: ApplicationStatusCellRendererComponent,
                cellRendererParams: (
                    params: ApplicationStatusCellRendererParams,
                ) =>
                    ({
                        status: params.data.filingStatus,
                        concurrenceDate: params.data.concurrenceDate,
                        releaseDate: params.data.releaseDate,
                    }) as ApplicationStatusCellRendererParams,
                filter: MultiSelectFilterComponent,
                filterParams: {
                    filterOptions: this.statusFilterOptions.map((status) => ({
                        label: status.label,
                        value: status.label,
                        selected: status.selected,
                    })),
                },
                filterValueGetter: (params) =>
                    ApplicationGridService.getStatusFilterValue(params.data),
            },
            {
                headerName: 'Application State',
                field: nameOf<ServiceFulfillmentGridRow>('workflowState'),
                valueGetter: (params) => {
                    const rowData = params.data as ServiceFulfillmentGridRow;
                    return rowData.workflowState != null
                        ? WorkflowStateLabels[rowData.workflowState]
                        : '';
                },
                filter: MultiSelectFilterComponent,
                filterParams: {
                    filterOptions: (this.workflowStateFilterOptions ?? []).map(
                        (option) => ({
                            label: option.label,
                            value: option.value,
                            selected: option.selected,
                        }),
                    ),
                },
                minWidth: 160,
            },
            {
                headerName: 'Action',
                field: nameOf<ServiceFulfillmentGridRow>(
                    'isCoordinationAllowed',
                ),
                cellRenderer: ApplicationActionCellRendererComponent,
                cellRendererParams: {
                    returnRoute:
                        RouteConstants.ServiceFulfillment.getFullRoute('INDEX'),
                },
                filter: false,
                sortable: false,
                maxWidth: 150,
                suppressSizeToFit: true,
            },
        ];
    }

    externalFilterChanged(): void {
        this.gridApi?.onFilterChanged();
    }

    private loadGridData() {
        this.serviceFulfillmentService
            .getAll()
            .pipe(
                takeUntil(this.destroy$),
                tap((data: ServiceFulfillmentGridRow[]) => {
                    this.rowData = data;
                    this.columnDefs = this.getColumnDefs();
                }),
                filter(() => !!this.gridApi),
            )
            .subscribe(() => {
                this.gridApi!.setGridOption('rowData', this.rowData);
                this.gridApi!.setGridOption('columnDefs', this.columnDefs);
            });
    }

    private loadFilterData(): Observable<[FilterOption[], FilterOption[]]> {
        return forkJoin([
            this.filterDataService.getApplicationPurposeFilterOptions(),
            this.filterDataService.getAdvisorFilterOptions(),
        ]).pipe(
            tap(([purposeOptions, advisorOptions]) => {
                this.servicesFilterOptions = [
                    ...purposeOptions,
                    DashboardConstants.FRN_REQUEST_FILTER_OPTION,
                ];
                this.advisorFilterOptions = advisorOptions;
            }),
        );
    }
}
